/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import { css } from "@emotion/react";
import type { Theme } from "@emotion/react";
import { RadioProps } from "../Radio";

export const hoverStyle = (
	theme: Theme,
	disable: RadioProps["disabled"],
	hideLabel: boolean,
	labelPosition: RadioProps["labelPosition"]
) => {
	return css`
		padding: 5px;
		margin: ${hideLabel ? "0" : labelPosition === "left" ? "0 0 0 7px" : "0 7px 0 0"};
		display: inline-flex;
		border-radius: 50%;
		transition: 0.4s;
		&:hover {
			background: ${disable ? "none" : theme.colors.secondary_80_12};

			> .dot {
				outline-color: ${disable ? theme.colors.neutral_80_32 : theme.colors.neutral_80_100};
			}
		}
	`;
};

export const labelTextStyle = (hideLabel: boolean) => css`
	margin: 0;
	display: ${hideLabel ? "none" : "flex"};
	font-size: 14px;
	align-items: center;
	justify-content: center;
`;

export const CommonStyles = (colors: Theme["colors"]) => css`
	content: "";
	display: inline-block;
	width: 10px;
	height: 10px;
	background-color: ${colors.neutral_00_100};
	position: absolute;
	transform-origin: left top;
`;

export const wrapperStyle = (theme: Theme) => {
	return css`
		display: flex;
		color: ${theme.colors.primary_90_100};
	`;
};

export const labelStyle = ({
	labelPosition
}: {
	hideLabel: boolean;
	labelPosition: DesignStuff.Checkbox.LabelPosition;
}) => {
	return css`
		position: relative;
		display: flex;
		align-items: center;
		flex-direction: ${labelPosition === "left" ? "row-reverse" : "row"};

		input {
			margin: ${labelPosition === "left" ? "0 0 0 5px" : "0 5px 0 0"};
		}
	`;
};

export const radioStyle = (theme: Theme, disabled: boolean, isChecked: boolean) => css`
	position: absolute;
	left: 0;
	right: 0;
	width: 28px;
	height: 28px;
	opacity: 0;

	+ div {
		> .dot {
			position: relative;
			display: flex;
			width: 18px;
			height: 18px;
			justify-content: center;
			cursor: pointer;
			outline-width: 1px;
			outline-style: solid;
			border-radius: 50%;
			outline-color: ${disabled ? theme.colors.neutral_80_32 : theme.colors.neutral_50_100};
			pointer-events: ${disabled ? "none" : "initial"};
			box-sizing: border-box;
		}
	}

	&:checked {
		+ div {
			> .dot {
				outline-color: ${disabled
					? theme.colors.neutral_80_32
					: isChecked
					? theme.colors.neutral_50_100
					: theme.colors.neutral_80_100};

				&::before {
					${CommonStyles(theme.colors)};
					display: flex;
					top: 50%;
					left: 50%;
					width: 10px;
					height: 10px;
					background: ${disabled ? theme.colors.neutral_80_32 : theme.colors.neutral_80_100};
					border-radius: 50%;
					transform: translate(-50%, -50%);
					transition: ${isChecked ? "all 0.15s ease" : "initial"};
				}
			}
		}
	}
`;
